"use client"
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DollarSign, Database, FileText, Clock, User, ImageIcon } from "lucide-react";
import { useAuth } from "../../AuthContext"; // Add this import
import { decryptId } from "../../utils/crypto";
import { useLanguage } from "../../contexts/LanguageContext";

export default function ProjectDetails() {
  const { projectlId } = useParams();
  const decryptedProjectId = decryptId(projectlId);
  const { user } = useAuth(); // Add this line
  const { t } = useLanguage();
  const [project, setProject] = useState();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [note, setNote] = useState("");
  const [requestedProjects, setRequestedProjects] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (decryptedProjectId) {
      fetchProjectDetails();
    }
  }, [decryptedProjectId]);

  const fetchProjectDetails = async () => {
    try {
      const response = await fetch(`http://localhost:5000/professional-projects-details/${decryptedProjectId}`);
      const data = await response.json();

      const encodedImages = [
        data.encoded_image1,
        data.encoded_image2,
        data.encoded_image3,
        data.encoded_image4,
        data.encoded_image5,
      ].filter(image => image !== null); // Filter out null images

      setProject({
        ...data,
        skills_used: data.skills_used ? JSON.parse(`[${data.skills_used}]`) : [],
        encoded_images: encodedImages.map(decodeImage), // Decode each non-null image
      });
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const decodeImage = (base64String) => {
    return `data:image/jpeg;base64,${base64String}`;
  };

  const toggleImageModal = () => {setIsImageModalOpen(!isImageModalOpen);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % project.encoded_images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + project.encoded_images.length) % project.encoded_images.length);
  };

  const handleRequestProject = async () => {
    const clientId = user?.secondId; // Use clientId from AuthContext
    if (!clientId) {
      alert(t("clientIdMissing"));
      return;
    }
    if (requestedProjects.includes(clientId)) {
      alert(t("alreadyRequestedProject"));
      return;
    }

    const formData = {
      title: project.project_title,
      description: project.project_description,
      projectType: project.type,
      budgetRange: `$${project.project_price}`,
      deadline: new Date(project.completion_date).toISOString().split("T")[0],
      note,
      status: "private", // Ensure status is included
      skills: project.skills_used.map(skill => skill.id), // Ensure skills array is mapped correctly
      clientId // Pass clientId to backend
    };

    try {
      const response = await fetch("http://localhost:5000/add-client-projects", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (!response.ok) {
        alert(result.error || t("requestProjectFailed"));
      } else {
        alert(t("requestProjectSuccess"));
        setRequestedProjects([...requestedProjects, clientId]);
        setShowRequestForm(false);

        // Add notification for the project request
        await fetch("http://localhost:5000/notifications", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: project.user_id, 
            client_project_id: result.clientProjectId, // Assuming the API returns the client project ID
            type: "link",
            message: t("projectRequested"),
          }),
        });
      }
    } catch (error) {
      console.error("Error requesting project:", error);
      alert(t("requestProjectError"));
    }
  };

  if (!project) {
    return <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0', fontSize: '1.1rem' }}>{t("loading")}</div>;
  }

  return (
    <div className="lg:col-span-2">
      <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '16px' }}>{project.project_title}</h2>

      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', marginBottom: '28px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--accent-cyan), var(--accent-violet))' }} />
        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '18px' }}>{t("projectDetails")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DetailItem icon={DollarSign} title={t("priceRange")} content={`$${project.project_price}`} />
          <DetailItem 
            icon={Database} 
            title={t("technologiesUsed")} 
            content={project.skills_used.map(skill => skill.name).join(", ") || t("notAvailable")} 
          />
          <DetailItem icon={FileText} title={t("projectType")} content={project.type} />
          <DetailItem icon={Clock} title={t("completionDate")} content= {new Date(project.completion_date).toLocaleDateString()} />
          <DetailItem icon={User} title={t("projectProfessional")} content={project.professional_name} />
          <div>
            <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
              <ImageIcon style={{ color: 'var(--accent-cyan)' }} size={18} /> {t("projectImages")}
            </h4>
            <p>
              <button style={{ color: 'var(--accent-cyan)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }} onClick={toggleImageModal}>
                {t("viewImages")}
              </button>
            </p>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', marginBottom: '28px' }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>{t("projectDescription")}</h3>
        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{project.project_description}</p>
      </div>
      {user?.role === 'client' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px' }}>
          <div className="space-y-4">
              <button
                style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 700, padding: '14px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '1rem', transition: 'opacity 0.2s' }}
                onClick={() => setShowRequestForm(true)}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                {t("requestForProject")}
              </button>
          </div>
        </div>
        )}

      {/* Modal for adding a note */}
      {showRequestForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', width: '400px', maxWidth: '90vw', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-cyan))' }} />
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>{t("addNote")}</h3>
            <textarea
              style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '12px', color: 'var(--text-primary)', resize: 'vertical', outline: 'none', fontSize: '0.9rem' }}
              rows="4"
              placeholder={t("addNotePlaceholder")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--accent-cyan)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setShowRequestForm(false)}
              >
                {t("cancel")}
              </button>
              <button
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
                onClick={handleRequestProject}
              >
                {t("submit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for images */}
      {isImageModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', width: '420px', maxWidth: '90vw' }}>
            <img
              src={project.encoded_images[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              style={{ width: '100%', height: 'auto', borderRadius: '10px', border: '1px solid var(--border-subtle)', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <button
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
                onClick={prevImage}
              >
                {t("previous")}
              </button>
              <button
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
                onClick={nextImage}
              >
                {t("next")}
              </button>
            </div>
            <button
              style={{ width: '100%', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
              onClick={toggleImageModal}
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ icon: Icon, title, content }) {
  return (
    <div>
      <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
        <Icon style={{ color: 'var(--accent-cyan)' }} size={18} /> {title}
      </h4>
      <p style={{ color: 'var(--text-secondary)' }}>{content}</p>
    </div>
  );
}

